import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateEncounterFromAppointmentMutation } from '../store/api';
import type { AppointmentResponse } from '../types';

/**
 * Hook for navigating to an encounter, creating one if necessary
 *
 * Handles the common pattern of:
 * 1. Check if appointment has an existing encounter
 * 2. If yes, navigate to it
 * 3. If no, create a new encounter from the appointment, then navigate
 *
 * @returns {Object} Hook state and methods
 * @returns {Function} navigateToEncounter - Function to trigger navigation
 * @returns {boolean} isLoading - Whether encounter is being created
 * @returns {Error} error - Any error from encounter creation
 *
 * @example
 * ```typescript
 * const { navigateToEncounter, isLoading } = useNavigateToEncounter();
 *
 * const handleClick = () => {
 *   navigateToEncounter(appointment);
 * };
 * ```
 */
export const useNavigateToEncounter = () => {
  const navigate = useNavigate();
  const [createEncounter, { isLoading, error, reset }] =
    useCreateEncounterFromAppointmentMutation();

  const navigateToEncounter = useCallback(
    async (
      appointment:
        | AppointmentResponse
        | { appointment_id: string; encounter?: { encounter_id?: string } | string | null },
    ) => {
      try {
        // Reset any previous error state
        reset();

        // Check if encounter already exists
        // Handle both full appointment objects and objects with just encounter property
        let encounterId: string | null = null;

        if (appointment.encounter) {
          if (typeof appointment.encounter === 'string') {
            encounterId = appointment.encounter;
          } else if (
            typeof appointment.encounter === 'object' &&
            'encounter_id' in appointment.encounter
          ) {
            const encounterObj = appointment.encounter as { encounter_id?: string };
            encounterId = encounterObj.encounter_id || null;
          }
        }

        if (encounterId) {
          // Check if encounter is completed/processing with a note - navigate directly to note
          if (typeof appointment.encounter === 'object' && appointment.encounter) {
            const encounterObj = appointment.encounter as {
              encounter_id?: string;
              status?: string;
              note_id?: string;
            };

            if (
              (encounterObj.status === 'completed' || encounterObj.status === 'processing') &&
              encounterObj.note_id
            ) {
              navigate(`/note/${encounterObj.note_id}`);
              return { encounterId, created: false, navigatedToNote: true };
            }
          }

          // Otherwise navigate to encounter
          navigate(`/encounters/${encounterId}`);
          return { encounterId, created: false };
        }

        // No encounter exists, create one
        if (!appointment.appointment_id) {
          throw new Error('Appointment ID is required to create an encounter');
        }

        const result = await createEncounter({
          appointmentId: appointment.appointment_id,
        }).unwrap();

        // Navigate to the newly created encounter
        navigate(`/encounters/${result.encounter_id}`);

        return { encounterId: result.encounter_id, created: true };
      } catch (err) {
        // Error is already captured in the mutation's error state
        console.error('Failed to navigate to encounter:', err);
        throw err;
      }
    },
    [navigate, createEncounter, reset],
  );

  return {
    navigateToEncounter,
    isLoading,
    error,
    reset,
  };
};
